import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { ErrorBanner } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { ProjectDialog } from '@/components/tracking/project-dialog';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatAmount, formatDate } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import {
  projectStatusLabel,
  useProjects,
  useTrackingMutation,
  useTrackingSettings,
  type Project,
  type ProjectStatus,
} from '@/lib/tracking';

export const Route = createFileRoute('/projects/')({ component: Projects });

const CHIPS: Array<ProjectStatus | 'all'> = ['in_progress', 'completed', 'cancelled', 'all'];

/** Screen PR1 (document 06): projects with income, costs and profit from the ledger. */
function Projects() {
  const t = useT();
  const orgId = useOrgId();
  const me = useMe();
  const settings = useTrackingSettings();
  const isStaff = me.data?.isStaff ?? false;
  const [status, setStatus] = useState<ProjectStatus | 'all'>('in_progress');
  const [q, setQ] = useState('');
  const list = useProjects({ status });
  const [editing, setEditing] = useState<Project | 'new' | null>(null);
  const act = useTrackingMutation(
    (o, v: { p: Project; status?: ProjectStatus; remove?: boolean }) =>
      v.remove
        ? api<unknown>(`/orgs/${o}/projects/${v.p.id}`, { method: 'DELETE' })
        : api<unknown>(`/orgs/${o}/projects/${v.p.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: v.status }),
          }),
  );
  if (!orgId) return <EmptyState title={t('shell.selectClient')} />;
  const f = q.trim().toLowerCase();
  const rows = (list.data ?? []).filter(
    (p) => !f || `${p.name} ${p.customerName}`.toLowerCase().includes(f),
  );
  return (
    <div>
      <PageHeader
        title={t('salesPages.projectList.title')}
        description={t('salesPages.projectList.description')}
        actions={
          isStaff ? (
            <Button onClick={() => setEditing('new')}>
              {t('salesPages.projectList.newProject')}
            </Button>
          ) : null
        }
      />
      {!settings.projects ? (
        <p className="mb-3 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {t('salesPages.projectList.off')}
        </p>
      ) : null}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {CHIPS.map((c) => (
          <Button
            key={c}
            size="sm"
            variant={status === c ? 'default' : 'outline'}
            onClick={() => setStatus(c)}
          >
            {c === 'all' ? t('salesPages.recordList.all') : projectStatusLabel(c)}
          </Button>
        ))}
        <Input
          aria-label={t('salesPages.projectList.searchLabel')}
          placeholder={t('salesPages.projectList.searchPlaceholder')}
          className="ms-auto w-72"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <ErrorBanner>{act.error?.message ?? null}</ErrorBanner>
      {list.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : list.isError ? (
        <EmptyState title={t('salesPages.projectList.loadError')} hint={list.error.message} />
      ) : !rows.length ? (
        <EmptyState
          title={t('salesPages.projectList.empty')}
          hint={t('salesPages.projectList.emptyHint')}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('salesPages.projectList.colProject')}</TableHead>
                <TableHead>{t('salesPages.projectList.colCustomer')}</TableHead>
                <TableHead>{t('salesPages.recordList.status')}</TableHead>
                <TableHead>{t('salesPages.projectList.colStartDate')}</TableHead>
                <TableHead>{t('salesPages.projectList.colEndDate')}</TableHead>
                <TableHead className="text-end">{t('salesPages.projectView.income')}</TableHead>
                <TableHead className="text-end">{t('salesPages.projectView.costs')}</TableHead>
                <TableHead className="text-end">{t('salesPages.projectView.profit')}</TableHead>
                <TableHead className="text-end">
                  {t('salesPages.projectView.openBalance')}
                </TableHead>
                <TableHead className="w-[140px]">{t('salesPages.recordList.action')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`project-${p.name}`}>
                  <TableCell>
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: p.id }}
                      className="font-medium hover:text-primary hover:underline"
                    >
                      {p.name}
                    </Link>
                  </TableCell>
                  <TableCell>{p.customerName}</TableCell>
                  <TableCell>{projectStatusLabel(p.status)}</TableCell>
                  <TableCell>{p.startDate ? formatDate(p.startDate) : ''}</TableCell>
                  <TableCell>{p.endDate ? formatDate(p.endDate) : ''}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(p.income)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(p.costs)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(p.profit)}</TableCell>
                  <TableCell className="tnum text-end">{formatAmount(p.openBalance)}</TableCell>
                  <TableCell>
                    {isStaff ? (
                      <div className="flex items-center">
                        <Button
                          variant="link"
                          size="sm"
                          className="px-1"
                          onClick={() => setEditing(p)}
                        >
                          {t('common.edit')}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={t('salesPages.recordList.moreActions', { name: p.name })}
                            >
                              <ChevronDown className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {(['in_progress', 'completed', 'cancelled'] as const)
                              .filter((s) => s !== p.status)
                              .map((s) => (
                                <DropdownMenuItem
                                  key={s}
                                  onSelect={() => act.mutate({ p, status: s })}
                                >
                                  {s === 'in_progress'
                                    ? t('salesPages.projectList.reopen')
                                    : s === 'completed'
                                      ? t('salesPages.projectList.markCompleted')
                                      : t('salesPages.projectList.markCancelled')}
                                </DropdownMenuItem>
                              ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() =>
                                act.mutate(
                                  { p, remove: true },
                                  {
                                    onSuccess: () =>
                                      toast(t('salesPages.projectList.deleted', { name: p.name })),
                                  },
                                )
                              }
                            >
                              {t('salesPages.projectList.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ProjectDialog project={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
